/**
 * Halaman Manajemen Pangkalan (Outlet Management)
 * Menyediakan CRUD operations untuk data pangkalan
 * Role Access: Admin & Manajer (full access), Checker (read only)
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Search, 
  ChevronLeft, 
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  X,
  Eye,
  EyeOff
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const ManajemenPangkalan = () => {
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [deleteAllConfirmOpen, setDeleteAllConfirmOpen] = useState(false);
  const [viewingOutlet, setViewingOutlet] = useState(null);
  const [showKTP, setShowKTP] = useState(false);
  
  // Selection states for bulk operations
  const [selectedOutlets, setSelectedOutlets] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    outlet_name: '',
    outlet_id: '',
    mid: '',
    owner_name: '',
    id_card_number: '',
    mobile_phone: '',
    phone_number: '',
    email: '',
    address: '',
    is_active: true
  });
  const [editingOutlet, setEditingOutlet] = useState(null);
  const [deletingOutlet, setDeletingOutlet] = useState(null);
  
  // User role state
  const [currentUser, setCurrentUser] = useState(null);
  
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
    setCurrentUser(user);
    fetchOutlets();
  }, [currentPage, itemsPerPage, searchTerm, filterStatus]);
  
  // Reset selection when page or data changes
  useEffect(() => {
    setSelectedOutlets([]);
    setSelectAll(false);
  }, [currentPage, outlets]);
  
  const fetchOutlets = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        sort_by: 'created_at',
        sort_order: 'desc'
      };
      
      if (searchTerm) params.search = searchTerm;
      if (filterStatus !== 'all') params.is_active = filterStatus === 'active';
      
      const response = await axios.get(`${API}/api/outlets`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setOutlets(response.data.data);
      setTotalItems(response.data.total);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      toast.error('Gagal memuat data pangkalan');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  const resetForm = () => {
    setFormData({
      outlet_name: '',
      outlet_id: '',
      mid: '',
      owner_name: '',
      id_card_number: '',
      mobile_phone: '',
      phone_number: '',
      email: '',
      address: '',
      is_active: true
    });
  };
  
  const handleCreate = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.outlet_name || !formData.outlet_id || !formData.owner_name || !formData.id_card_number) {
      toast.error('Mohon lengkapi field yang wajib diisi');
      return;
    }
    
    // Trim and validate KTP
    const ktpNumber = formData.id_card_number.trim();
    if (ktpNumber.length !== 16) {
      toast.error('Nomor KTP harus 16 digit');
      return;
    }
    
    if (!/^\d{16}$/.test(ktpNumber)) {
      toast.error('Nomor KTP harus berisi 16 digit angka');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/outlets`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pangkalan berhasil ditambahkan');
      setCreateDialogOpen(false);
      resetForm();
      setCurrentPage(1);
      fetchOutlets();
    } catch (error) {
      console.error('Error creating outlet:', error);
      
      // Handle different error formats
      let errorMsg = 'Gagal menambahkan pangkalan';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // If detail is an array of validation errors
        if (Array.isArray(detail)) {
          errorMsg = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } 
        // If detail is a string
        else if (typeof detail === 'string') {
          errorMsg = detail;
        }
        // If detail is an object
        else if (typeof detail === 'object') {
          errorMsg = detail.msg || JSON.stringify(detail);
        }
      }
      
      toast.error(errorMsg);
    }
  };
  
  const handleEdit = (outlet) => {
    setEditingOutlet(outlet);
    setFormData({
      outlet_name: outlet.outlet_name,
      outlet_id: outlet.outlet_id,
      mid: outlet.mid || '',
      owner_name: outlet.owner_name,
      id_card_number: outlet.id_card_number,
      mobile_phone: outlet.mobile_phone || '',
      phone_number: outlet.phone_number || '',
      email: outlet.email || '',
      address: outlet.address || '',
      is_active: outlet.is_active
    });
    setEditDialogOpen(true);
  };
  
  const handleViewDetail = (outlet) => {
    setViewingOutlet(outlet);
    setShowKTP(false);
    setDetailDialogOpen(true);
  };
  
  const maskKTP = (ktp) => {
    if (!ktp || ktp.length !== 16) return ktp;
    return `${ktp.substring(0, 4)}****${ktp.substring(12)}`;
  };
  
  const handleUpdate = async (e) => {
    e.preventDefault();
    
    if (!formData.outlet_name || !formData.owner_name || !formData.id_card_number) {
      toast.error('Mohon lengkapi field yang wajib diisi');
      return;
    }
    
    // Trim and validate KTP
    const ktpNumber = formData.id_card_number.trim();
    if (ktpNumber.length !== 16) {
      toast.error('Nomor KTP harus 16 digit');
      return;
    }
    
    if (!/^\d{16}$/.test(ktpNumber)) {
      toast.error('Nomor KTP harus berisi 16 digit angka');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const updateData = { ...formData };
      delete updateData.outlet_id; // ID cannot be changed
      
      await axios.put(`${API}/api/outlets/${editingOutlet.outlet_id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pangkalan berhasil diupdate');
      setEditDialogOpen(false);
      setEditingOutlet(null);
      resetForm();
      fetchOutlets();
    } catch (error) {
      console.error('Error updating outlet:', error);
      
      // Handle different error formats
      let errorMsg = 'Gagal mengupdate pangkalan';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // If detail is an array of validation errors
        if (Array.isArray(detail)) {
          errorMsg = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } 
        // If detail is a string
        else if (typeof detail === 'string') {
          errorMsg = detail;
        }
        // If detail is an object
        else if (typeof detail === 'object') {
          errorMsg = detail.msg || JSON.stringify(detail);
        }
      }
      
      toast.error(errorMsg);
    }
  };
  
  const handleDelete = (outlet) => {
    setDeletingOutlet(outlet);
    setDeleteConfirmOpen(true);
  };
  
  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/outlets/${deletingOutlet.outlet_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pangkalan berhasil dihapus');
      setDeleteConfirmOpen(false);
      setDeletingOutlet(null);
      fetchOutlets();
    } catch (error) {
      toast.error('Gagal menghapus pangkalan');
      console.error(error);
    }
  };
  
  // Checkbox selection handlers
  const handleSelectOutlet = (outletId) => {
    setSelectedOutlets(prev => {
      if (prev.includes(outletId)) {
        return prev.filter(id => id !== outletId);
      } else {
        return [...prev, outletId];
      }
    });
  };
  
  const handleSelectAll = () => {
    if (selectAll || selectedOutlets.length === outlets.length) {
      setSelectedOutlets([]);
      setSelectAll(false);
    } else {
      setSelectedOutlets(outlets.map(o => o.outlet_id));
      setSelectAll(true);
    }
  };
  
  // Bulk delete handler
  const handleBulkDelete = () => {
    if (selectedOutlets.length === 0) {
      toast.error('Pilih minimal satu pangkalan untuk dihapus');
      return;
    }
    setBulkDeleteConfirmOpen(true);
  };
  
  const confirmBulkDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/outlets/bulk`, {
        headers: { Authorization: `Bearer ${token}` },
        data: selectedOutlets
      });
      toast.success(`Berhasil menghapus ${selectedOutlets.length} pangkalan`);
      setBulkDeleteConfirmOpen(false);
      setSelectedOutlets([]);
      setSelectAll(false);
      fetchOutlets();
    } catch (error) {
      toast.error('Gagal menghapus pangkalan');
      console.error(error);
    }
  };
  
  // Delete all handler
  const handleDeleteAll = () => {
    setDeleteAllConfirmOpen(true);
  };
  
  const confirmDeleteAll = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/outlets/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Semua pangkalan berhasil dihapus');
      setDeleteAllConfirmOpen(false);
      setSelectedOutlets([]);
      setSelectAll(false);
      fetchOutlets();
    } catch (error) {
      toast.error('Gagal menghapus pangkalan');
      console.error(error);
    }
  };
  
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };
  
  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1);
  };
  
  const canEdit = currentUser?.role === 'Admin' || currentUser?.role === 'Manajer';
  
  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-emerald-800">
            Manajemen Pangkalan
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Kelola data pangkalan LPG
          </p>
        </div>
        {canEdit && (
          <Button 
            onClick={() => {
              resetForm();
              setCreateDialogOpen(true);
            }}
            className="bg-emerald-600 hover:bg-emerald-700 w-full md:w-auto"
          >
            <Plus className="mr-2 h-4 w-4" />
            Tambah Pangkalan
          </Button>
        )}
      </div>
      
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Cari nama, ID, pemilik, atau KTP..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterStatus} onValueChange={(value) => {
              setFilterStatus(value);
              setCurrentPage(1);
            }}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Status</SelectItem>
                <SelectItem value="active">Aktif</SelectItem>
                <SelectItem value="inactive">Tidak Aktif</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      
      {/* Stats & Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 text-sm text-gray-600">
          <div>
            Total: <span className="font-semibold text-emerald-700">{totalItems}</span> pangkalan
          </div>
          {selectedOutlets.length > 0 && (
            <div className="flex items-center gap-2">
              <div className="text-sm font-semibold text-blue-600">
                {selectedOutlets.length} terpilih
              </div>
              {canEdit && (
                <Button
                  onClick={handleBulkDelete}
                  size="sm"
                  variant="destructive"
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Trash2 className="mr-1 h-3 w-3" />
                  Hapus Terpilih
                </Button>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Baris per halaman:</span>
          <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
            <SelectTrigger className="w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
          {canEdit && (
            <Button
              onClick={handleDeleteAll}
              size="sm"
              variant="outline"
              className="border-red-600 text-red-600 hover:bg-red-50"
            >
              <Trash2 className="mr-1 h-3 w-3" />
              Hapus Semua
            </Button>
          )}
        </div>
      </div>
      
      {/* Table - Desktop */}
      <div className="hidden md:block rounded-md border border-emerald-200 overflow-hidden">
        <Table>
          <TableHeader className="bg-emerald-600">
            <TableRow>
              {canEdit && (
                <TableHead className="text-white font-semibold w-12 text-center">
                  <Checkbox
                    checked={selectAll || (outlets.length > 0 && selectedOutlets.length === outlets.length)}
                    onCheckedChange={handleSelectAll}
                    className="border-white data-[state=checked]:bg-white data-[state=checked]:text-emerald-600"
                  />
                </TableHead>
              )}
              <TableHead className="text-white font-semibold">Nama Pangkalan</TableHead>
              <TableHead className="text-white font-semibold">ID Pangkalan</TableHead>
              <TableHead className="text-white font-semibold">Nama Pemilik</TableHead>
              <TableHead className="text-white font-semibold text-center">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={canEdit ? 5 : 4} className="text-center py-8 text-gray-500">
                  Memuat data...
                </TableCell>
              </TableRow>
            ) : outlets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canEdit ? 5 : 4} className="text-center py-8 text-gray-500">
                  Belum ada data pangkalan
                </TableCell>
              </TableRow>
            ) : (
              outlets.map((outlet, index) => (
                <TableRow key={outlet.id} className={index % 2 === 0 ? 'bg-white' : 'bg-emerald-50/30'}>
                  {canEdit && (
                    <TableCell className="text-center">
                      <Checkbox
                        checked={selectedOutlets.includes(outlet.outlet_id)}
                        onCheckedChange={() => handleSelectOutlet(outlet.outlet_id)}
                      />
                    </TableCell>
                  )}
                  <TableCell className="font-medium">{outlet.outlet_name}</TableCell>
                  <TableCell>{outlet.outlet_id}</TableCell>
                  <TableCell>{outlet.owner_name}</TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewDetail(outlet)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        title="Lihat Detail"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {canEdit && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(outlet)}
                            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(outlet)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Hapus"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      
      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="text-center py-8 text-gray-500">Memuat data...</div>
        ) : outlets.length === 0 ? (
          <div className="text-center py-8 text-gray-500">Belum ada data pangkalan</div>
        ) : (
          outlets.map((outlet) => (
            <Card key={outlet.id} className={selectedOutlets.includes(outlet.outlet_id) ? 'ring-2 ring-blue-500' : ''}>
              <CardContent className="p-4 space-y-3">
                <div className="flex justify-between items-start">
                  <div className="flex items-start gap-2 flex-1">
                    {canEdit && (
                      <Checkbox
                        checked={selectedOutlets.includes(outlet.outlet_id)}
                        onCheckedChange={() => handleSelectOutlet(outlet.outlet_id)}
                        className="mt-1 flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-lg text-emerald-800 truncate">{outlet.outlet_name}</div>
                      <div className="text-sm text-gray-600">ID: {outlet.outlet_id}</div>
                    </div>
                  </div>
                  <Badge variant={outlet.is_active ? 'success' : 'secondary'} className={outlet.is_active ? 'bg-green-100 text-green-800 flex-shrink-0 ml-2' : 'bg-gray-100 text-gray-800 flex-shrink-0 ml-2'}>
                    {outlet.is_active ? 'Aktif' : 'Tidak Aktif'}
                  </Badge>
                </div>
                
                <div className="space-y-1 text-sm">
                  <div><span className="text-gray-600">Pemilik:</span> <span className="font-medium">{outlet.owner_name}</span></div>
                </div>
                
                <div className="flex gap-2 pt-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewDetail(outlet)}
                    className="flex-1 text-blue-600 border-blue-600 hover:bg-blue-50"
                  >
                    <Eye className="mr-1 h-3 w-3" />
                    Detail
                  </Button>
                  {canEdit && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(outlet)}
                        className="flex-1 text-amber-600 border-amber-600 hover:bg-amber-50"
                      >
                        <Pencil className="mr-1 h-3 w-3" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(outlet)}
                        className="flex-1 text-red-600 border-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="mr-1 h-3 w-3" />
                        Hapus
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-gray-600">
            Halaman {currentPage} dari {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm px-3">
              {currentPage}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
      
      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-emerald-800">
              Tambah Pangkalan Baru
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreate} className="space-y-6">
            {/* Section 1: Identitas Pangkalan */}
            <div className="space-y-4">
              <h3 className="text-md font-semibold text-emerald-700 border-b pb-2">
                Identitas Pangkalan LPG 3 Kg
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Nama Pangkalan *</Label>
                  <Input
                    value={formData.outlet_name}
                    onChange={(e) => setFormData({...formData, outlet_name: e.target.value})}
                    required
                    maxLength={200}
                  />
                </div>
                <div>
                  <Label>ID Pangkalan *</Label>
                  <Input
                    value={formData.outlet_id}
                    onChange={(e) => setFormData({...formData, outlet_id: e.target.value})}
                    required
                    maxLength={200}
                  />
                </div>
                <div>
                  <Label>MID</Label>
                  <Input
                    value={formData.mid}
                    onChange={(e) => setFormData({...formData, mid: e.target.value})}
                    maxLength={20}
                    placeholder="Contoh: LPG_186654"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Alamat</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="Alamat lengkap pangkalan"
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Informasi Pemilik */}
            <div className="space-y-4">
              <h3 className="text-md font-semibold text-emerald-700 border-b pb-2">
                Informasi Pemilik Pangkalan LPG 3 Kg
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Nama Pemilik *</Label>
                  <Input
                    value={formData.owner_name}
                    onChange={(e) => setFormData({...formData, owner_name: e.target.value})}
                    required
                    maxLength={150}
                  />
                </div>
                <div>
                  <Label>Nomor KTP * (16 digit)</Label>
                  <Input
                    value={formData.id_card_number}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setFormData({...formData, id_card_number: value});
                    }}
                    required
                    maxLength={16}
                    pattern="\d{16}"
                    title="Nomor KTP harus 16 digit angka"
                    placeholder="Contoh: 3201234567890123"
                  />
                </div>
                <div>
                  <Label>Nomor Ponsel</Label>
                  <Input
                    value={formData.mobile_phone}
                    onChange={(e) => setFormData({...formData, mobile_phone: e.target.value})}
                    maxLength={20}
                    placeholder="Contoh: 08123456789"
                  />
                </div>
                <div>
                  <Label>Nomor Telepon</Label>
                  <Input
                    value={formData.phone_number}
                    onChange={(e) => setFormData({...formData, phone_number: e.target.value})}
                    maxLength={20}
                    placeholder="Contoh: 021123456"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    maxLength={255}
                    placeholder="email@contoh.com"
                  />
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="flex items-center gap-2 pt-2">
              <Checkbox
                id="create-is-active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
              />
              <Label htmlFor="create-is-active" className="cursor-pointer">
                Pangkalan Aktif
              </Label>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                Simpan
              </Button>
              <Button 
                type="button"
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  setCreateDialogOpen(false);
                  resetForm();
                }}
              >
                Batal
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-amber-800">
              Edit Pangkalan
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleUpdate} className="space-y-6">
            {/* Section 1: Identitas Pangkalan */}
            <div className="space-y-4">
              <h3 className="text-md font-semibold text-amber-700 border-b pb-2">
                Identitas Pangkalan LPG 3 Kg
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Nama Pangkalan *</Label>
                  <Input
                    value={formData.outlet_name}
                    onChange={(e) => setFormData({...formData, outlet_name: e.target.value})}
                    required
                    maxLength={200}
                  />
                </div>
                <div>
                  <Label>ID Pangkalan</Label>
                  <Input
                    value={formData.outlet_id}
                    disabled
                    className="bg-gray-100"
                  />
                </div>
                <div>
                  <Label>MID</Label>
                  <Input
                    value={formData.mid}
                    onChange={(e) => setFormData({...formData, mid: e.target.value})}
                    maxLength={20}
                    placeholder="Contoh: LPG_186654"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Alamat</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="Alamat lengkap pangkalan"
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Informasi Pemilik */}
            <div className="space-y-4">
              <h3 className="text-md font-semibold text-amber-700 border-b pb-2">
                Informasi Pemilik Pangkalan LPG 3 Kg
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Nama Pemilik *</Label>
                  <Input
                    value={formData.owner_name}
                    onChange={(e) => setFormData({...formData, owner_name: e.target.value})}
                    required
                    maxLength={150}
                  />
                </div>
                <div>
                  <Label>Nomor KTP * (16 digit)</Label>
                  <Input
                    value={formData.id_card_number}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setFormData({...formData, id_card_number: value});
                    }}
                    required
                    maxLength={16}
                    pattern="\d{16}"
                    title="Nomor KTP harus 16 digit angka"
                    placeholder="Contoh: 3201234567890123"
                  />
                </div>
                <div>
                  <Label>Nomor Ponsel</Label>
                  <Input
                    value={formData.mobile_phone}
                    onChange={(e) => setFormData({...formData, mobile_phone: e.target.value})}
                    maxLength={20}
                    placeholder="Contoh: 08123456789"
                  />
                </div>
                <div>
                  <Label>Nomor Telepon</Label>
                  <Input
                    value={formData.phone_number}
                    onChange={(e) => setFormData({...formData, phone_number: e.target.value})}
                    maxLength={20}
                    placeholder="Contoh: 021123456"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    maxLength={255}
                    placeholder="email@contoh.com"
                  />
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="flex items-center gap-2 pt-2">
              <Checkbox
                id="edit-is-active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
              />
              <Label htmlFor="edit-is-active" className="cursor-pointer">
                Pangkalan Aktif
              </Label>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button type="submit" className="flex-1 bg-amber-600 hover:bg-amber-700">
                Update
              </Button>
              <Button 
                type="button"
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  setEditDialogOpen(false);
                  setEditingOutlet(null);
                  resetForm();
                }}
              >
                Batal
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={(open) => {
        setDetailDialogOpen(open);
        if (!open) setShowKTP(false);
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-800">
              Detail Pangkalan
            </DialogTitle>
          </DialogHeader>
          
          {viewingOutlet && (
            <div className="space-y-6">
              {/* Section 1: Identitas Pangkalan */}
              <div className="space-y-3">
                <h3 className="text-md font-semibold text-blue-700 border-b pb-2">
                  Identitas Pangkalan LPG 3 Kg
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-600">Nama Pangkalan</Label>
                    <p className="font-medium mt-1">{viewingOutlet.outlet_name}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">ID Pangkalan</Label>
                    <p className="font-medium mt-1">{viewingOutlet.outlet_id}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">MID</Label>
                    <p className="font-medium mt-1">{viewingOutlet.mid || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Status</Label>
                    <div className="mt-1">
                      <Badge variant={viewingOutlet.is_active ? 'success' : 'secondary'} 
                        className={viewingOutlet.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                        {viewingOutlet.is_active ? 'Aktif' : 'Tidak Aktif'}
                      </Badge>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-gray-600">Alamat</Label>
                    <p className="font-medium mt-1">{viewingOutlet.address || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Section 2: Informasi Pemilik */}
              <div className="space-y-3">
                <h3 className="text-md font-semibold text-blue-700 border-b pb-2">
                  Informasi Pemilik Pangkalan LPG 3 Kg
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-600">Nama Pemilik</Label>
                    <p className="font-medium mt-1">{viewingOutlet.owner_name}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Nomor KTP</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="font-mono font-medium">
                        {showKTP ? viewingOutlet.id_card_number : maskKTP(viewingOutlet.id_card_number)}
                      </p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowKTP(!showKTP)}
                        className="h-6 w-6 p-0"
                        title={showKTP ? 'Sembunyikan KTP' : 'Tampilkan KTP'}
                      >
                        {showKTP ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-600">Nomor Ponsel</Label>
                    <p className="font-medium mt-1">{viewingOutlet.mobile_phone || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Nomor Telepon</Label>
                    <p className="font-medium mt-1">{viewingOutlet.phone_number || '-'}</p>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-gray-600">Email</Label>
                    <p className="font-medium mt-1 text-blue-600">{viewingOutlet.email || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Timestamps */}
              <div className="pt-4 border-t text-xs text-gray-500 space-y-1">
                <div>Dibuat: {new Date(viewingOutlet.created_at).toLocaleString('id-ID')}</div>
                {viewingOutlet.updated_at && (
                  <div>Diupdate: {new Date(viewingOutlet.updated_at).toLocaleString('id-ID')}</div>
                )}
              </div>

              <div className="flex justify-end pt-4">
                <Button 
                  type="button"
                  onClick={() => setDetailDialogOpen(false)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Tutup
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Konfirmasi Penghapusan</AlertDialogTitle>
            <AlertDialogDescription>
              Apakah Anda yakin ingin menghapus pangkalan <span className="font-bold">{deletingOutlet?.outlet_name}</span>? 
              Tindakan ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingOutlet(null)}>
              Batal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteConfirmOpen} onOpenChange={setBulkDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Konfirmasi Penghapusan Massal</AlertDialogTitle>
            <AlertDialogDescription>
              Apakah Anda yakin ingin menghapus <span className="font-bold text-red-600">{selectedOutlets.length} pangkalan</span> yang dipilih? 
              Tindakan ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmBulkDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Hapus {selectedOutlets.length} Pangkalan
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Delete All Confirmation Dialog */}
      <AlertDialog open={deleteAllConfirmOpen} onOpenChange={setDeleteAllConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">⚠️ PERINGATAN: Hapus Semua Data</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p className="font-semibold">
                Tindakan ini akan menghapus SEMUA pangkalan yang ada di database!
              </p>
              <p>
                Anda akan kehilangan <span className="font-bold text-red-600">{totalItems} pangkalan</span> secara permanen. 
                Tindakan ini <span className="font-bold">TIDAK DAPAT DIBATALKAN</span>.
              </p>
              <p className="text-amber-600">
                Apakah Anda benar-benar yakin?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteAll}
              className="bg-red-600 hover:bg-red-700"
            >
              Ya, Hapus Semua
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ManajemenPangkalan;
